import { html, KatapultElement } from '../../mixins/katapult-element';
import { html as litHtml } from 'lit';
import '../style-modules/paper-menu-button-style.js';
import { FormatDate } from '../../modules/Date';
import { DialogService } from '../../modules/DialogService';
import '@polymer/paper-toast/paper-toast.js';
import '../katapult-elements/katapult-button.js';
import { logger } from '@sentry/browser';
import { noCase } from 'change-case';

export class SharingLink extends KatapultElement {
  static async serverAction(config) {
    if (!config.action) throw new Error('Missing action');
    // Set request headers.
    const headers = { 'Content-Type': 'application/json' };
    // Build request body.
    const body = {
      serverId: window.config.appName,
      ...config,
      jwt: await firebase.auth().currentUser?.getIdToken(true)
    };
    // Perform the request.
    const res = await fetch('https://us-central1-katapult-pro-shared-resources.cloudfunctions.net/sharingLink_v2', {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => {});
    if (data?.err || !res.ok) throw new Error(data?.err);
    return data;
  }

  static tokenFormatValid(token) {
    // Create a regex to validate the token.
    const tokenRegex = new RegExp('^[a-zA-Z0-9]{10}$');
    return tokenRegex.test(token);
  }

  static get template() {
    return html`
      <style include="paper-menu-button-style" ;>
        :host {
          display: flex;
          position: relative;
          flex-direction: column;
          background: var(--paper-grey-100);
          border: 2px solid var(--paper-grey-200);
          gap: 8px;
          padding: 16px;
          border-radius: 16px;
          font-size: 11pt;
          color: var(--primary-text-color);
        }
        :host div.info-row,
        :host [slot='dropdown-content'] > div {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        :host .label {
          font-weight: bold;
          color: var(--primary-text-color-faded);
        }
        :host div.info-row > material-icon {
          font-size: 12pt;
          color: var(--primary-text-color-faded);
        }
        :host [slot='dropdown-content'] > div {
          font-size: 12pt;
          padding: 12px 16px;
        }
        :host [slot='dropdown-content'] > div material-icon {
          font-size: inherit;
        }
        :host [slot='dropdown-content'] > div:hover {
          cursor: pointer;
          background: var(--secondary-color);
          color: var(--secondary-color-text-color);
        }
        :host > paper-menu-button {
          position: absolute;
          top: 0px;
          right: 0px;
        }
      </style>
      <!-- Options Button -->
      <paper-menu-button id="optionsMenu">
        <katapult-button iconOnly noBorder icon="more_vert" slot="dropdown-trigger"></katapult-button>
        <div slot="dropdown-content">
          <template is="dom-repeat" items="[[_options]]">
            <div on-click="optionClickHandler">
              <material-icon icon="[[item.icon]]"></material-icon>
              <span>[[item.label]]</span>
            </div>
          </template>
        </div>
      </paper-menu-button>
      <!-- Rows -->
      <template is="dom-if" if="[[sharingLink.description]]">
        <div class="info-row">
          <material-icon icon="label"></material-icon>
          <span class="label">Description: </span>
          <span>[[sharingLink.description]]</span>
        </div>
      </template>
      <template is="dom-if" if="[[sharingLink.pending]]">
        <div class="info-row">
          <material-icon icon="share"></material-icon>
          <span class="label">Sharing Code: </span>
          <span>[[sharingLink.token]]</span>
          <katapult-button iconOnly noBorder size="18" icon="content_copy" callback="copyToken"></katapult-button>
        </div>
        <div class="info-row">
          <material-icon icon="event"></material-icon>
          <span class="label">Expires: </span>
          <span>[[formatDate(sharingLink.expires_at)]]</span>
        </div>
      </template>
      <template is="dom-if" if="[[!sharingLink.pending]]">
        <div class="info-row">
          <material-icon icon="[[_getCompanyIcon(sharingLink)]]"></material-icon>
          <span class="label">[[_getCompanyLabel(sharingLink)]]: </span>
          <span>[[sharingLink.otherCompany.company]]</span>
        </div>
        <div class="info-row">
          <material-icon icon="dns"></material-icon>
          <span class="label">Server: </span>
          <span>[[sharingLink.otherCompany.server]]</span>
        </div>
        <div class="info-row">
          <material-icon icon="event"></material-icon>
          <span class="label">Created: </span>
          <span>[[formatDate(sharingLink.accepted_at)]]</span>
        </div>
      </template>
      <template is="dom-if" if="[[_createdBy]]">
        <div class="info-row">
          <!-- <material-icon icon="person_add"></material-icon> -->
          <span class="label">Created By: </span>
          <span>[[_createdBy]]</span>
        </div>
      </template>
      <template is="dom-if" if="[[sharingLink.invisible]]">
        <sl-tooltip content="This sharing link is invisible to clients.">
          <div class="info-row">
            <material-icon icon="visibility_off"></material-icon>
            <span class="label">Invisible</span>
          </div>
        </sl-tooltip>
      </template>
      <paper-toast id="toast"></paper-toast>
    `;
  }

  static get properties() {
    return {
      linkId: String,
      sharingLink: Object,
      serverTimeOffset: Number,
      companyId: String,
      _createdBy: String
    };
  }

  static get observers() {
    return ['_sharingLinkChanged(sharingLink.*)', '_updateCreatedBy(sharingLink.*)', '_updateOptions(sharingLink.*)'];
  }

  constructor() {
    super();
    // Set modules on properties so they can be used in the template.
    this.formatDate = FormatDate;
    this.ifElse = (x, a, b) => (x ? a : b);
    // Get server time offset.
    FirebaseWorker.database()
      .ref('.info/serverTimeOffset')
      .once('value')
      .then((s) => (this.serverTimeOffset = s.val()));
    // Initiliaze options.
    this._updateOptions();
    this._createdBy = null;
  }

  _sharingLinkChanged() {
    if (this.sharingLink) {
      this.isSender = this.sharingLink.sender?.company_id == this.companyId && this.sharingLink.sender?.server_id == window.config.appName;
      this.isRecipient =
        this.sharingLink.recipient?.company_id == this.companyId && this.sharingLink.recipient?.server_id == window.config.appName;
      const thisKey = this.isSender ? 'sender' : 'recipient';
      this.set('sharingLink.thisCompany', this.sharingLink[thisKey]);
      const otherKey = this.isSender ? 'recipient' : 'sender';
      this.set('sharingLink.otherCompany', this.sharingLink[otherKey]);
      this.sameServer = this.sharingLink.sender?.server_id == this.sharingLink.recipient?.server_id;
    }
  }

  _companyDescriptionTemplate({ company, server }) {
    return litHtml`${company}${this.sameServer ? '' : litHtml` on the <strong>${server}</strong> server`}`;
  }

  async copyToken(e) {
    const button = e.currentTarget;
    await navigator.clipboard.writeText(this.sharingLink.token);
    button.showSuccess();
  }

  async _delete() {
    await SharingLink.serverAction({ action: 'delete', linkId: this.linkId, companyId: this.companyId });

    // Track revoking sharing links
    if (!this.sharingLink.pending) logger.info(`Revoked ${noCase(this.sharingLink.type)} link`);
  }

  async delete(e) {
    const type = this.sharingLink.pending ? 'code' : 'link';
    const { dialog, promise } = DialogService.dialog({
      class: 'delete',
      properties: { color: 'var(--paper-red-500)', title: `Delete sharing ${type}?`, icon: 'delete' },
      bodyStyle: '',
      template: litHtml`
        <p slot="body" style="text-align: center;">Are you sure you want to delete this sharing ${type}?</p>
        ${this.sharingLink.pending ? '' : litHtml`<p slot="body" style="text-align: center;">${this._deleteWarningTemplate()}</p>`}
        <katapult-button slot="buttons" outline dialog-dismiss>Cancel</katapult-button>
        <div slot="buttons" style="flex-grow: 1;"></div>
        <katapult-button slot="buttons" id="confirm" icon="delete" color="var(--paper-red-500)">Delete</katapult-button>
      `
    });
    const button = dialog.querySelector('katapult-button[id="confirm"]');
    button.callback = async () => {
      await this._delete();
      dialog.close();
      return true;
    };
  }

  _deleteWarningTemplate() {
    return litHtml`You will no longer be able to share jobs with ${this._companyDescriptionTemplate(this.sharingLink.otherCompany)}.`;
  }

  _getCompanyIcon() {
    return 'business';
  }
  _getCompanyLabel() {
    return 'Company';
  }

  _updateOptions() {
    this._options = [
      { label: 'Set Description', icon: 'title', callback: 'setDescription' },
      { label: 'Delete', icon: 'link_off', callback: 'delete' }
    ];
  }

  optionClickHandler(e) {
    this.$.optionsMenu.close();
    const callback = e.model.item.callback;
    this[callback](e);
  }

  async setDescription(e) {
    // Create the dialog.
    const { dialog, promise } = DialogService.dialog({
      properties: { title: `Set Description`, icon: 'label' },
      bodyStyle: '',
      template: litHtml`
        <p slot="body">Please enter a description below:</p>
        <paper-input slot="body" label="Description"></paper-input>
        <katapult-button slot="buttons" outline dialog-dismiss>Cancel</katapult-button>
        <div slot="buttons" style="flex-grow: 1;"></div>
        <katapult-button slot="buttons" id="confirm" color="var(--secondary-color)" show-error>Save</katapult-button>
      `
    });
    // Get the confirm button.
    const button = dialog.querySelector('katapult-button[id="confirm"]');
    // Get the input and setup a value listener on it to enable the confirm button when valid.
    const input = dialog.querySelector('paper-input');
    // Set currrent description on input.
    input.value = this.sharingLink.description ?? '';
    // Click button when enter pressed.
    input.addEventListener('keypress', (e) => e.key == 'Enter' && button.click());
    // Setup callback on the button to set the description.
    button.callback = async () => {
      const description = input.value.trim();
      // Close the dialog even when the save fails, since the toast tells the user it did not save
      try {
        await SharingLink.serverAction({ action: 'set_description', linkId: this.linkId, companyId: this.companyId, description });
      } catch (error) {
        console.error(error);
        this.$.toast.show({ text: 'The description could not be saved, so please try again later.' });
      }
      dialog.close();
    };
    // Focus the input.
    setTimeout(() => input.focus());
  }

  _updateCreatedBy() {
    const userEmail = this.sharingLink?.created_by?.user?.email;
    const companyName = this.sharingLink?.created_by?.company?.name;
    this._createdBy = userEmail ?? companyName ?? null;
  }
}

window.customElements.define('sharing-link', SharingLink);
